import React from 'react';
import PropTypes from 'prop-types';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import GaugeLineGraph from './GaugeLineGraph';
import { getGaugeFields } from '../../utils/gaugeDataFormatUtils';
import {
    getMetricSourceForRequest,
    hasMetricSources,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import { getFormattedTimeFromMilliseconds } from '../../utils/dataFormatUtils';

/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics }) {
    if (
        metricRequests.length === 0 ||
        (metricRequests[0].groupBy.length === 0 &&
            metricRequests[0].metrics.length === 0)
    ) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const metricSource = getMetricSourceForRequest(metrics, metricRequests[0]);
    if (!metricSource) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized([metricSource])) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources([metricSource])) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [metricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { metricSource, metricRequest: metricRequests[0] };
}

export default function GaugeLineGraphWrapper(props) {
    const { metricSource, metricRequest, metricSourceErrors, errorKey } =
        getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const gaugeFields = getGaugeFields(
        metricRequest,
        metricSource,
        props.numberDecimalFormatter
    );

    const dataFormatter = props.configuration.isTime
        ? getFormattedTimeFromMilliseconds
        : props.numberDecimalFormatter;

    return (
        <>
            {metricSource.isTruncated && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${metricRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container">
                <GaugeLineGraph
                    {...props}
                    dataFormatter={dataFormatter}
                    dataLabels={gaugeFields.dataLabels}
                    dataPoints={gaugeFields.formattedData}
                    chartProperties={props.configuration}
                    firstGroupKey={gaugeFields.groupBy}
                />
            </div>
        </>
    );
}
/* eslint-enable complexity */

GaugeLineGraphWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
};
